import { Button, Group, NumberInput, Pagination as MantinePagination, Text, useMatches } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useState } from "react";

import { useIsMobile } from "@/shared/hooks";

import type { PaginationProps } from "./types";

export default function Pagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
}: PaginationProps) {
  const [pageInput, setPageInput] = useState<number | string>("");
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  const isMobile = useIsMobile();

  const handleGoToPage = () => {
    const page = Number(pageInput);

    if (Number.isNaN(page)) return;
    if (page < 1 || page > totalPages) return;

    onPageChange(page);
    setPageInput("");
  };

  const siblings = useMatches({
    base: 0,
    sm: 1,
  });
  const boundaries = useMatches({
    base: 0,
    sm: 1,
  });

  return (
    <Group justify="space-between" align="center" gap="md">
      <Group gap="xs" align="center">
        <Group gap="xs">
          <Button size="xs" variant="outline" onClick={handleGoToPage}>
            برو به صفحه
          </Button>
          <NumberInput
            value={pageInput}
            onChange={setPageInput}
            min={1}
            max={totalPages}
            size="xs"
            w={48}
            hideControls
          />
        </Group>

        {totalItems > 0 && !isMobile && (
          <Text size="xs" c="dimmed" fz="sm">
            نمایش{" "}
            <Text span fw={600} c="dark" fz="sm">
              {startItem} تا {endItem}
            </Text>{" "}
            مورد از مجموع{" "}
            <Text span fw={600} c="dark" fz="sm">
              {totalItems}
            </Text>{" "}
            مورد
          </Text>
        )}
      </Group>

      <Group gap="xs" align="center">
        <Button
          variant="outline"
          size="compact-xs"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          <IconChevronRight size={14} />
        </Button>

        <MantinePagination
          value={currentPage}
          onChange={onPageChange}
          total={totalPages}
          size="xs"
          siblings={siblings}
          boundaries={boundaries}
          withControls={false}
          color="secondary.9"
        />

        <Button
          variant="outline"
          size="compact-xs"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          <IconChevronLeft size={14} />
        </Button>
      </Group>
    </Group>
  );
}
